import axios from 'axios';
import { getSessionUUID } from '@utils/uuid';
import {  emitDebugLog } from '@utils/logger';

const API_CONFIG = {
  baseURL: 'https://lw-demoprimary-sales.b.lucidworks.cloud',
  application: 'Build_Demo',
  queryProfile: 'Build_Demo',
  apiKey: '8b0754fc-652c-448c-88fd-96c03bbcf4eb',
  timeout: 10000,
};

const QUERY_PROFILES = {
  'default': 'Build_Demo',
  'typeahead': 'build_demo_typeahead',
  'suggestions': 'build_demo_typeahead',
  'landing-page': 'build_demo_landing',
  'pdp': 'build_demo_pdp',
  'category': 'build_demo_category',
};

const DEFAULT_PARAMS = {
  query: '*:*',
  rows: 12,
  start: 0
};

const ERROR_MESSAGES = {
  401: 'Authentication failed. Please check your API credentials.',
  403: 'You don\'t have permission to access this resource.',
  404: 'The requested resource was not found.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again later.',
  network: 'Network error. Please check your connection.'
};

const TYPEAHEAD_FIELDS = [
  'id', 'title', 'name', 'display_name_s', 'text', 'description', 'description_t',
  'url', 'type', 'image_url', 'image_url_s', 'image', 'imageuri', 'images', 'variant_images'
];

// Cache for session and user IDs
let cachedSessionId = null;
const getUserId = () => 'anonymous';
const getSessionId = () => {
  if (!cachedSessionId) {
    cachedSessionId = getSessionUUID();
  }
  return cachedSessionId;
};

const validateConfig = () => {
  const required = ['baseURL', 'application', 'queryProfile', 'apiKey'];
  const missing = required.filter(key => !API_CONFIG[key]);
  if (missing.length > 0) {
    return false;
  }
  return true;
};

// Shared axios client for all API calls, including fusion_rag
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_CONFIG.apiKey
  }
});

// Optimized error handler
const handleApiError = (error) => {
  const status = error.response?.status;
  const isServerError = status >= 500 && status <= 504;
  
  if (!error.response) {
    return new Error(ERROR_MESSAGES.network);
  }
  
  const errorMessage = ERROR_MESSAGES[status] || 
    (isServerError ? ERROR_MESSAGES[500] : error.message);
  
  return new Error(errorMessage);
};

apiClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    return Promise.reject(handleApiError(error));
  }
);

// Optimized parameter building
const buildSearchParams = (searchOptions) => {
  const {
    query, start, rows, filterQueries = [],
    collapse, collapseField, expand, expandField, fields,
    facet, facetLimit, facetMinCount, sort
  } = searchOptions;

  const params = new URLSearchParams({
    q: query,
    start: start.toString(),
    rows: rows.toString(),
    user_id: getUserId(),
    session: getSessionId()
  });

  // Add sort parameter if specified
  if (sort && sort !== 'score desc') {
    params.append('sort', sort);
  }

  // Add filter queries
  filterQueries.forEach(fq => params.append('fq', fq));

  // Add facet parameters
  if (facet) {
    params.append('facet', 'true');
    if (facetLimit) params.append('facet.limit', facetLimit.toString());
    if (facetMinCount) params.append('facet.mincount', facetMinCount.toString());
  }

  // Add collapse/expand parameters
  if (collapse) {
    params.append('fq', `{!collapse field=${collapseField}}`);
    if (expand) {
      params.append('expand', 'true');
      params.append('expand.field', collapseField);
      if (expandField) {
        params.append('expand.fl', expandField);
      }
    }
  }

  // Add fields if specified
  if (fields) {
    // params.append('fl', Array.isArray(fields) ? fields.join(',') : fields);
  }

  return params;
};

// Optimized query processing
const processQuery = (query, id) => {
  if (id && !query.includes('id:')) {
    const escapedId = id.replace(/([!:+&|<>=^(){}[\]^"~*?\\/-])/g, '\\$1');
    return `id:"${escapedId}"`;
  }
  return query;
};

// Optimized result processing
const processSearchResult = (response, id, startTime) => {
  const elapsedMs = Date.now() - startTime;
  let documents = response.data.response?.docs || [];

  if (id) {
    documents = documents.filter(doc => doc.id === id);
  }

  return {
    success: true,
    data: response.data,
    totalResults: response.data.response?.numFound || 0,
    documents: documents,
    facets: response.data.facet_counts || null,
    responseTime: response.data.responseHeader?.QTime || 0,
    elapsedTime: elapsedMs
  };
};

export const search = async (options = {}) => {
  const searchOptions = { ...DEFAULT_PARAMS, ...options };
  const { query, queryProfile, signal, id, sort } = searchOptions;
  
  
  if (!validateConfig()) {
    const errorMsg = 'API configuration is incomplete';
    emitDebugLog(`❌ ${errorMsg}`);
    return { success: false, error: errorMsg, data: null };
  }
  
  const startTime = Date.now();
  
  try {
    const processedQuery = processQuery(query, id);
    const profileEndpoint = QUERY_PROFILES[queryProfile] || QUERY_PROFILES.default;
    const params = buildSearchParams({ ...searchOptions, query: processedQuery });
    const requestUrl = `/api/apps/${API_CONFIG.application}/query/${profileEndpoint}`;

    emitDebugLog(`🌐 Request: ${API_CONFIG.baseURL}${requestUrl}${sort ? ` (sorted by ${sort})` : ''}`);

    const response = await apiClient.get(requestUrl, { params, signal });
    const result = processSearchResult(response, id, startTime);

    emitDebugLog(`✅ Response: ${result.totalResults} results in ${result.elapsedTime}ms${sort ? ` (sorted by ${sort})` : ''}`);
    return result;

  } catch (error) {
    const isAborted = error.name === 'AbortError' || error.name === 'CanceledError' || axios.isCancel(error);
    const errorMessage = isAborted ? 'Search was cancelled' : error.message;

    emitDebugLog(`❌ Error: ${errorMessage}`);

    return {
      success: false,
      error: errorMessage,
      data: null,
      cancelled: isAborted
    };
  }
};

export const getTypeaheadSuggestions = async (query, signal = null) => {

  if (!query || query.trim().length < 2) {
    return { success: false, error: 'Query too short', suggestions: [] };
  }
  
  try {
    const result = await search({
      query: `${query}*`,
      rows: 10,
      queryProfile: 'typeahead',
      signal,
      fields: TYPEAHEAD_FIELDS
    });
    
    if (!result.success) {
      return { ...result, suggestions: [] };
    }
    
    const suggestions = result.documents.map((doc, index) => {
      const getImageUrl = () => {
        const imageFields = ['image_url', 'image_url_s', 'image', 'imageuri'];
        for (const field of imageFields) {
          if (doc[field]) return doc[field];
        }
        if (Array.isArray(doc.images) && doc.images[0]) return doc.images[0];
        if (Array.isArray(doc.variant_images) && doc.variant_images[0]) return doc.variant_images[0];
        return null;
      };

      return {
        id: doc.id || `suggestion_${index}`,
        title: doc.title || doc.name || doc.display_name_s || doc.text || 'Untitled',
        description: doc.description || doc.description_t || doc.content?.substring(0, 100) || '',
        url: doc.url || null,
        type: doc.type || 'product',
        image: getImageUrl(),
        originalDoc: doc
      };
    });
    
    return {
      success: true,
      suggestions,
      data: result.data
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      suggestions: []
    };
  }
};

export const facetedSearch = (searchParams = {}, signal = null) => {
  const result = search({
    ...searchParams,
    facet: true,
    signal
  });
  
  return result;
};

export const getPage = (lastSearch = {}, page = 1, signal = null) => {
  const rows = lastSearch.rows || DEFAULT_PARAMS.rows;
  const start = (page - 1) * rows;

  const result = search({
    ...lastSearch,
    start,
    signal
  });
  
  return result;
};

export const getHomePageProducts = (limit = 12, signal = null) => {
  const result = search({
    query: '*:*',
    rows: limit,
    queryProfile: 'landing-page',
    signal
  });

  return result;
};

export const fusion_rag = async (query, context, productId = null, signal = null) => {
  
  if (!validateConfig()) {
    const errorMsg = 'API configuration is incomplete';
    emitDebugLog(`❌ ${errorMsg}`);
    return { success: false, error: errorMsg, data: null };
  }

  const startTime = Date.now();

  // List of RAG endpoints to try in order
  const ragEndpoints = [
    'build_demo_rag',
    'Build_Demo_rag', 
    'rag',
    'ui_rag',
    'Build_Demo' // fallback to default profile
  ];

  // Use productId for fq
  let finalProductId = productId;
  if (!finalProductId && context) {
    finalProductId = context.id || context._id || context.productId || context.product_id || context.document_id || context.docId;
  }

  const params = new URLSearchParams({
    q: query,
    user_id: getUserId(),
    session: getSessionId(),
  });

  if (finalProductId) {
    const escapedId = finalProductId.toString().replace(/([!:+&|<>=^(){}[\]^"~*?\\/-])/g, '\\$1');
    params.append('fq', `id:("${escapedId}")`);
  }

  // Try each RAG endpoint
  for (const endpoint of ragEndpoints) {
    try {
      const requestUrl = `/api/apps/${API_CONFIG.application}/query/${endpoint}`;
      const fullUrl = `${API_CONFIG.baseURL}${requestUrl}`;
      emitDebugLog(`🌐 RAG Request: ${fullUrl}`);

      // Make the request with explicit headers to ensure auth is included
      const response = await apiClient.get(requestUrl, { 
        params, 
        signal,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_CONFIG.apiKey
        }
      });

      const elapsedMs = Date.now() - startTime;
      
      // Extract RAG response from the Fusion response format
      let predictions = null;
      if (response.data.fusion?.grounded_response) {
        const groundedResponse = response.data.fusion.grounded_response;
        
        // Extract just the answer text from the grounded response
        let answerText = groundedResponse;
        
        // Try multiple patterns to extract the answer
        let answerMatch = groundedResponse.match(/ANSWER:\s*\\"(.*?)\\"/);
        if (!answerMatch) {
          answerMatch = groundedResponse.match(/ANSWER:\s*"(.*?)"/);
        }
        if (!answerMatch) {
          answerMatch = groundedResponse.match(/ANSWER:\s*(.+?)(?:\n\n|$)/s);
        }
        
        if (answerMatch && answerMatch[1]) {
          answerText = answerMatch[1]
            .replace(/\\"/g, '"')
            .replace(/\\n/g, '\n')
            .trim();
        }
        
        predictions = [{ answer: answerText }];
      } else {
        // If no grounded_response, create a basic answer from search results
        const docs = response.data.response?.docs || [];
        if (docs.length > 0) {
          const doc = docs[0];
          const basicAnswer = `Based on the product information: ${doc.title_t || doc.title_s || 'Product'} - ${doc.description_t || doc.description_s || 'No description available'}`.substring(0, 500);
          predictions = [{ answer: basicAnswer }];
        }
      }
      
      const result = {
        success: true,
        data: response.data,
        predictions: predictions,
        responseTime: response.data.responseHeader?.QTime || 0,
        elapsedTime: elapsedMs,
        usedEndpoint: endpoint
      };
      
      emitDebugLog(`✅ RAG Response from ${endpoint} in ${result.elapsedTime}ms`);
      return result;

    } catch (error) {

      // Log the error but continue to next endpoint unless it's the last one
      const isLastEndpoint = endpoint === ragEndpoints[ragEndpoints.length - 1];

      // If this is the last endpoint, return the error
      if (isLastEndpoint) {
        const errorMessage = error.message;

        emitDebugLog(`❌ RAG Error: ${errorMessage}`);
        return {
          success: false,
          error: `All RAG endpoints failed. Last error: ${errorMessage}`,
          data: null,
          cancelled: false,
          triedEndpoints: ragEndpoints
        };
      }
    }
  }
};

const searchApi = {
  search,
  facetedSearch,
  getPage,
  getTypeaheadSuggestions,
  getHomePageProducts,
  fusion_rag
};

export default searchApi;